<script setup>
import { computed, ref, watch } from 'vue';

import { useContainerWidth } from '../composables/useContainerWidth';
import HrSideSheet from '../primitives/HrSideSheet.vue';

/**
 * The M3 list-detail canonical layout: a list and the item chosen from it.
 *
 * It answers to its own width, never the window's, so it adapts the same way whether or not a
 * navigation drawer is open. From 840px it shows the list and the detail side by side. Below that
 * it shows one pane: the detail when an item is open, with the list in a side sheet from 600px and
 * behind Back below it. With nothing open, the list takes the whole width. Side by side, a divider
 * separates the panes.
 *
 * @status recommended
 * @group layout
 * @useWhen Working through a list one item at a time, where the list stays the place the user returns to
 * @avoidWhen The item is short-lived and the list is not where work happens — open it in a dialog
 * @related HrRecordsSection
 * @a11y The list is a region labelled by `listLabel`; the detail is named by its own heading. The side
 *   sheet is modal and traps focus.
 */
defineOptions({ name: 'HrListDetailLayout' });

const props = defineProps({
  /**
   * Which item the detail shows, or null when none is open. A change closes the side sheet, so
   * picking an item from it lands on that item.
   */
  detailKey: {
    type: String,
    default: null,
  },
  /**
   * What the list is, used as the label of its region and the title of the side sheet.
   * @example Tasks
   */
  listLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits({
  /** The user asked to leave the detail, from a single-pane layout. The host closes the item. */
  back: null,
});

// M3's window-size classes, applied to the space this layout is given. A container query cannot
// read a custom property, so these are numbers rather than tokens.
const EXPANDED_FROM = 840;
const MEDIUM_FROM = 600;

const root = ref(null);
const width = useContainerWidth(root);

const mode = computed(() => {
  if (width.value >= EXPANDED_FROM) {
    return 'expanded';
  }
  return width.value >= MEDIUM_FROM ? 'medium' : 'compact';
});

const detailOpen = computed(() => props.detailKey !== null);
const showsListPane = computed(() => mode.value === 'expanded' || !detailOpen.value);
const listSheetOpen = ref(false);

watch(() => props.detailKey, () => {
  listSheetOpen.value = false;
});
watch(mode, (next) => {
  if (next !== 'medium') {
    listSheetOpen.value = false;
  }
});

const showList = () => {
  listSheetOpen.value = true;
};
const back = () => emit('back');
</script>

<template>
  <div
    ref="root"
    class="hr-list-detail-layout"
    :class="`hr-list-detail-layout--${mode}`"
  >
    <section
      v-if="showsListPane"
      class="hr-list-detail-layout__list"
      :class="{ 'hr-list-detail-layout__list--alone': !detailOpen }"
      :aria-label="listLabel"
    >
      <!-- @slot The list. Receives { mode }. -->
      <slot
        name="list"
        v-bind="{ mode }"
      />
    </section>
    <section
      v-if="detailOpen"
      class="hr-list-detail-layout__detail"
    >
      <!-- @slot The open item. Receives { mode, showList, back }: open the list sheet, or leave the item. -->
      <slot
        name="detail"
        v-bind="{ mode, showList, back }"
      />
    </section>
    <hr-side-sheet
      v-if="mode === 'medium' && detailOpen"
      v-model="listSheetOpen"
      edge="start"
      :title="listLabel"
    >
      <!-- @slot The same list, shown in the side sheet. Receives { mode }. -->
      <slot
        name="list"
        v-bind="{ mode }"
      />
    </hr-side-sheet>
  </div>
</template>

<style lang="scss" scoped>
.hr-list-detail-layout {
  display: flex;
  min-height: 0;
}

/*
 * Two panes read as two: a divider between them, with the same space on each side of it, so the
 * list and the detail sit at the same distance from the line that separates them.
 */
.hr-list-detail-layout--expanded .hr-list-detail-layout__detail {
  border-inline-start: 1px solid var(--app-outline-variant);
  margin-inline-start: var(--app-comp-list-detail-pane-gap);
  padding-inline-start: var(--app-comp-list-detail-pane-gap);
}

.hr-list-detail-layout__list {
  flex: none;
  width: var(--app-comp-list-detail-list-width);
  min-width: 0;
}

.hr-list-detail-layout__list--alone,
.hr-list-detail-layout--medium .hr-list-detail-layout__list,
.hr-list-detail-layout--compact .hr-list-detail-layout__list {
  flex: 1 1 auto;
  width: auto;
}

.hr-list-detail-layout__detail {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
